/**
 * OrcamentoPage.jsx
 *
 * ── PDF mostra dados do sub-cliente quando selecionado
 * ── "Usar como venda" → resolve itens contra estoque, grava no localStorage
 *    e redireciona para /venda (PDV existente)
 * ── Itens sem estoque aparecem marcados no PDV com _semEstoque: true
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import OrcamentoPDF from "./OrcamentoPDF";
import api from "../../api/api";
import Swal from "sweetalert2";
import styles from "./OrcamentoPage.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const COLOR_PRESETS = [
  { label: "Azul (padrão)", value: "#1A56DB" },
  { label: "Azul escuro",   value: "#1344B3" },
  { label: "Verde",         value: "#0D9F6E" },
  { label: "Roxo",          value: "#7C3AED" },
  { label: "Vermelho",      value: "#DC2626" },
  { label: "Laranja",       value: "#D97706" },
  { label: "Cinza escuro",  value: "#374151" },
  { label: "Preto",         value: "#111827" },
];

const STORAGE_COLOR_KEY = "orcamento_primary_color";
const PDV_STORAGE_KEY   = "pdv_itens";
const PDV_DESCONTO_KEY  = "pdv_desconto";
const PDV_ORC_KEY       = "pdv_orcamento_origem";

const STATUS_LABEL = {
  aberto:     "Aberto",
  aprovado:   "Aprovado",
  recusado:   "Recusado",
  convertido: "Convertido",
};

const STATUS_CLASS = {
  aberto:     styles.statusAberto,
  aprovado:   styles.statusAprovado,
  recusado:   styles.statusRecusado,
  convertido: styles.statusConvertido,
};

const EMPTY_ITEM = () => ({ nome: "", quantidade: 1, valor_unitario: 0, produto_id: null, tipo: "manual" });

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatBRL = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const toBase64 = async (url) => {
  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error();
    const blob = await r.blob();
    return await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.onerror  = rej;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

const calcTotal = (itens, desconto = 0) => {
  const sub = itens.reduce((a, it) => a + Number(it.valor_unitario) * Number(it.quantidade), 0);
  return Math.max(0, sub - Number(desconto));
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlusIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const TrashIcon   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const SearchIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const FileIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const CartIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>;
const WarnIcon    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const RefreshIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>;
const SaveIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const UserIcon    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;

const EditIcon    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;

// ─── ColorPicker ──────────────────────────────────────────────────────────────

const ColorPicker = memo(({ value, onChange }) => (
  <div className={styles.colorPickerWrapper}>
    <span className={styles.label}>Cor do orçamento</span>
    <div className={styles.colorPresets}>
      {COLOR_PRESETS.map((p) => (
        <button key={p.value} type="button"
          className={`${styles.colorDot} ${value === p.value ? styles.colorDotActive : ""}`}
          style={{ backgroundColor: p.value }}
          onClick={() => onChange(p.value)}
          title={p.label} aria-label={p.label} aria-pressed={value === p.value}
        />
      ))}
      <label className={styles.colorCustomLabel} title="Cor personalizada">
        <input type="color" className={styles.colorCustomInput} value={value}
          onChange={(e) => onChange(e.target.value)} aria-label="Cor personalizada" />
        <span className={styles.colorCustomIcon}>+</span>
      </label>
    </div>
    <span className={styles.colorPreview} style={{ backgroundColor: value }} />
  </div>
));
ColorPicker.displayName = "ColorPicker";

// ─── ItemRow ──────────────────────────────────────────────────────────────────

const ItemRow = memo(({ item, index, onChange, onRemove, produtos }) => {
  const [sugestoes, setSugestoes] = useState([]);
  const [dropOpen,  setDropOpen]  = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleBusca = useCallback((v) => {
    if (!v.trim()) { setSugestoes([]); setDropOpen(false); return; }
    const t = v.toLowerCase();
    const match = produtos.filter((p) => p.nome.toLowerCase().includes(t) || p.ean?.includes(t)).slice(0, 6);
    setSugestoes(match);
    setDropOpen(match.length > 0);
  }, [produtos]);

  const selecionarProduto = (prod) => {
    onChange(index, {
      nome:           prod.nome,
      produto_id:     prod.id,
      tipo:           prod.tipo ?? "produto",
      valor_unitario: prod.valor_venda ?? prod.preco_custo ?? 0,
      em_estoque:     prod.em_estoque,
    });
    setSugestoes([]);
    setDropOpen(false);
  };

  const isLinked = Boolean(item.produto_id);

  return (
    <tr className={`${styles.itemRow} ${item.em_estoque === false ? styles.itemRowWarn : ""}`}>
      <td className={styles.tdNome}>
        <div className={styles.nomeCell} ref={wrapRef}>
          {isLinked ? (
            <div className={styles.nomeProduto}>
              <span>{item.nome}</span>
              <span className={`${styles.tipoBadge} ${styles[`tipo_${item.tipo}`]}`}>{item.tipo}</span>
              {item.em_estoque === false && (
                <span className={styles.warnBadge}><WarnIcon /> sem estoque</span>
              )}
              <button type="button" className={styles.btnUnlink}
                onClick={() => onChange(index, { nome: item.nome, produto_id: null, tipo: "manual", em_estoque: true })}
                title="Desvincular produto">×</button>
            </div>
          ) : (
            <>
              <input type="text" className={styles.inputInline}
                value={item.nome}
                placeholder="Nome do item ou busque um produto..."
                onChange={(e) => {
                  onChange(index, { nome: e.target.value });
                  handleBusca(e.target.value);
                }}
              />
              <div className={styles.buscaWrap}>
                <button type="button" className={styles.btnBusca}
                  onClick={() => handleBusca(item.nome || " ")} title="Buscar produto">
                  <SearchIcon />
                </button>
                {dropOpen && (
                  <div className={styles.sugestoes}>
                    {sugestoes.map((p) => (
                      <button key={p.id} type="button" className={styles.sugestaoItem}
                        onClick={() => selecionarProduto(p)}>
                        <span className={styles.sugestaoNome}>{p.nome}</span>
                        <span className={styles.sugestaoInfo}>
                          {p.em_estoque === false
                            ? <span className={styles.semEstoque}><WarnIcon /> sem estoque</span>
                            : formatBRL(p.valor_venda ?? p.preco_custo ?? 0)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </td>

      <td className={styles.tdQtd}>
        <input type="number" className={styles.inputQtd} min={1} value={item.quantidade}
          onChange={(e) => onChange(index, { quantidade: Math.max(1, parseInt(e.target.value) || 1) })} />
      </td>

      <td className={styles.tdPreco}>
        <input type="number" className={styles.inputPreco} min={0} step={0.01} value={item.valor_unitario}
          onChange={(e) => onChange(index, { valor_unitario: parseFloat(e.target.value) || 0 })} />
      </td>

      <td className={styles.tdSub}>
        <strong>{formatBRL(item.quantidade * item.valor_unitario)}</strong>
      </td>

      <td className={styles.tdAcao}>
        <button type="button" className={styles.btnRemoveItem}
          onClick={() => onRemove(index)} aria-label="Remover item"><TrashIcon /></button>
      </td>
    </tr>
  );
});
ItemRow.displayName = "ItemRow";

// ─── EditOrcamentoModal ───────────────────────────────────────────────────────

const EditOrcamentoModal = memo(({ orc, subClientes, produtos, onSave, onClose }) => {
  const [subClienteId,  setSubClienteId]  = useState(String(orc.sub_cliente_id ?? ""));
  const [itens,         setItens]         = useState(
    (orc.itens ?? []).map((it) => ({
      nome:           it.nome,
      quantidade:     it.quantidade,
      valor_unitario: it.valor_unitario,
      produto_id:     it.produto_id ?? null,
      tipo:           it.tipo ?? "manual",
      em_estoque:     it.em_estoque ?? true,
    }))
  );
  const [observacoes,   setObservacoes]   = useState(orc.observacoes   ?? "");
  const [footerMessage, setFooterMessage] = useState(orc.footer_message ?? "");
  const [desconto,      setDesconto]      = useState(Number(orc.desconto) || 0);
  const [primaryColor,  setPrimaryColor]  = useState(orc.primary_color ?? "#1A56DB");
  const [saving,        setSaving]        = useState(false);

  const handleChangeItem  = useCallback((i, patch) => setItens((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it)), []);
  const handleAddItem     = useCallback(() => setItens((prev) => [...prev, EMPTY_ITEM()]), []);
  const handleRemoveItem  = useCallback((i) => setItens((prev) => prev.filter((_, idx) => idx !== i)), []);

  const total = useMemo(() => calcTotal(itens, desconto), [itens, desconto]);

  const subClienteAtual = useMemo(
    () => subClientes.find((s) => String(s.id) === String(subClienteId)) ?? null,
    [subClientes, subClienteId]
  );

  const handleSubmit = async () => {
    if (itens.every((it) => !it.nome.trim())) {
      Swal.fire("Atenção", "Adicione ao menos um item.", "warning");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        sub_cliente_id: subClienteId || null,
        observacoes:    observacoes  || null,
        footer_message: footerMessage || null,
        primary_color:  primaryColor,
        desconto:       Number(desconto) || 0,
        itens: itens.filter((it) => it.nome.trim()).map((it) => ({
          produto_id:     it.produto_id || null,
          nome:           it.nome,
          quantidade:     Number(it.quantidade),
          valor_unitario: Number(it.valor_unitario),
          tipo:           it.tipo || "manual",
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true"
      aria-labelledby="edit-orc-title"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className={styles.modalBox}>

        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 id="edit-orc-title" className={styles.modalTitle}>Editar Orçamento</h2>
          <button type="button" className={styles.modalClose}
            onClick={onClose} disabled={saving} aria-label="Fechar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Cliente */}
          <div className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>Para quem é este orçamento?</h3>
            <select className={styles.select} value={subClienteId}
              onChange={(e) => setSubClienteId(e.target.value)} disabled={saving}>
              <option value="">— Sem cliente vinculado —</option>
              {subClientes.map((sc) => (
                <option key={sc.id} value={sc.id}>{sc.nome}</option>
              ))}
            </select>
            {subClienteAtual && (
              <div className={styles.subClientePreview}>
                <div className={styles.subClientePreviewIcon}><UserIcon /></div>
                <div className={styles.subClientePreviewInfo}>
                  <strong>{subClienteAtual.nome}</strong>
                  {subClienteAtual.email    && <span>{subClienteAtual.email}</span>}
                  {subClienteAtual.telefone && <span>{subClienteAtual.telefone}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Itens */}
          <div className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>Itens</h3>
            <div className={styles.tabelaWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Produto / Serviço</th>
                    <th style={{ width: 70 }}>Qtd.</th>
                    <th style={{ width: 120 }}>Preço unit.</th>
                    <th style={{ width: 100 }}>Subtotal</th>
                    <th style={{ width: 36 }} />
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, i) => (
                    <ItemRow key={i} index={i} item={it}
                      onChange={handleChangeItem} onRemove={handleRemoveItem} produtos={produtos} />
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className={styles.totalLabel}>
                      <div className={styles.descontoRow}>
                        <span>Desconto (R$)</span>
                        <input type="number" className={styles.inputDesconto}
                          min={0} step={0.01} value={desconto}
                          onChange={(e) => setDesconto(parseFloat(e.target.value) || 0)}
                          disabled={saving} />
                      </div>
                    </td>
                    <td colSpan={2} className={styles.totalValue}>{formatBRL(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <button type="button" className={styles.btnAddItem} onClick={handleAddItem} disabled={saving}>
              <PlusIcon /> Adicionar item
            </button>
          </div>

          {/* Complementos */}
          <div className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>Complementos</h3>
            <div className={styles.complementosGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Observações</label>
                <textarea className={styles.textarea} rows={3}
                  value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Condições de pagamento, validade..." disabled={saving} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Mensagem de rodapé</label>
                <input type="text" className={styles.input}
                  value={footerMessage} onChange={(e) => setFooterMessage(e.target.value)}
                  placeholder="Ex: Agradecemos a preferência!" disabled={saving} />
              </div>
            </div>
          </div>

          {/* Cor */}
          <div className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>Cor do PDF</h3>
            <ColorPicker value={primaryColor} onChange={setPrimaryColor} />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnCancel}
            onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className={styles.btnSalvar}
            onClick={handleSubmit} disabled={saving} aria-busy={saving}>
            {saving
              ? <><span className={styles.spinner} /> Salvando...</>
              : <><SaveIcon /> Salvar alterações</>}
          </button>
        </div>
      </div>
    </div>
  );
});
EditOrcamentoModal.displayName = "EditOrcamentoModal";

// ─── OrcamentoCard ────────────────────────────────────────────────────────────

const OrcamentoCard = memo(({ orc, cliente, subClientes, produtos, onConverter, onExcluir, onStatus, onEditar }) => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const subClientePDF = orc.sub_cliente_nome
    ? {
        nome:     orc.sub_cliente_nome,
        cpf_cnpj: orc.sub_cliente_cpf_cnpj ?? "",
        email:    orc.sub_cliente_email    ?? "",
        telefone: orc.sub_cliente_telefone ?? "",
        endereco: orc.sub_cliente_endereco ?? "",
      }
    : null;

  const pdfProps = useMemo(() => ({
    cliente: {
      nome:     cliente?.nome     ?? "",
      cnpj_cpf: cliente?.cnpj_cpf ?? "",
      email:    cliente?.email    ?? "",
      telefone: cliente?.telefone ?? "",
      endereco: cliente?.endereco ?? "",
    },
    logo:          cliente?.logo_base64 ?? null,
    subCliente:    subClientePDF,
    produtos:      (orc.itens ?? []).map((it) => ({
      nome:       it.nome,
      quantidade: it.quantidade,
      preco:      it.valor_unitario,
    })),
    observacoes:   orc.observacoes    ?? "",
    footerMessage: orc.footer_message ?? "",
    primaryColor:  orc.primary_color  ?? "#1A56DB",
  }), [orc, cliente]);

  const temAviso      = (orc.itens ?? []).some((it) => it.em_estoque === false);
  const podeConverter = orc.status !== "convertido" && orc.status !== "recusado";
  const podeEditar    = orc.status !== "convertido";

  const handleSaveEdit = useCallback(async (payload) => {
    await onEditar(orc.id, payload);
    setEditOpen(false);
  }, [orc.id, onEditar]);

  return (
    <>
      <div className={`${styles.orcCard} ${orc.status === "convertido" ? styles.orcCardConverted : ""}`}>
      <div className={styles.orcCardHeader} onClick={() => setExpanded((v) => !v)}>
        <div className={styles.orcCardLeft}>
          <span className={`${styles.statusBadge} ${STATUS_CLASS[orc.status]}`}>
            {STATUS_LABEL[orc.status] ?? orc.status}
          </span>
          <div className={styles.orcCardInfo}>
            <span className={styles.orcCardNome}>
              {orc.sub_cliente_nome
                ? <><UserIcon />&nbsp;{orc.sub_cliente_nome}</>
                : <span className={styles.orcCardSemCliente}>Sem cliente vinculado</span>}
            </span>
            <span className={styles.orcCardData}>
              {new Date(orc.criado_em).toLocaleDateString("pt-BR")} · {formatBRL(orc.valor_total)}
            </span>
          </div>
        </div>
        <div className={styles.orcCardRight}>
          {temAviso && (
            <span className={styles.avisoEstoque}><WarnIcon /> Estoque</span>
          )}
          <span className={styles.expandIcon}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div className={styles.orcCardBody}>

          {/* Sub-cliente detalhes */}
          {subClientePDF && (
            <div className={styles.subClienteBox}>
              <span className={styles.subClienteLabel}>Para</span>
              <div className={styles.subClienteInfo}>
                <strong>{subClientePDF.nome}</strong>
                {subClientePDF.cpf_cnpj && <span>{subClientePDF.cpf_cnpj}</span>}
                {subClientePDF.email    && <span>{subClientePDF.email}</span>}
                {subClientePDF.telefone && <span>{subClientePDF.telefone}</span>}
                {subClientePDF.endereco && <span>{subClientePDF.endereco}</span>}
              </div>
            </div>
          )}

          {/* Tabela de itens */}
          <div className={styles.orcItensWrapper}>
            <table className={styles.orcItensTable}>
              <thead>
                <tr><th>Item</th><th>Qtd</th><th>Unit.</th><th>Subtotal</th><th>Tipo</th></tr>
              </thead>
              <tbody>
                {(orc.itens ?? []).map((it, i) => (
                  <tr key={i} className={it.em_estoque === false ? styles.rowWarn : ""}>
                    <td>
                      {it.nome}
                      {it.em_estoque === false && (
                        <span className={styles.warnInline}><WarnIcon /> sem estoque</span>
                      )}
                    </td>
                    <td>{it.quantidade}</td>
                    <td>{formatBRL(it.valor_unitario)}</td>
                    <td><strong>{formatBRL(it.valor_total)}</strong></td>
                    <td><span className={`${styles.tipoBadge} ${styles[`tipo_${it.tipo}`]}`}>{it.tipo}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className={styles.totalLabel}>Total</td>
                  <td colSpan={2} className={styles.totalValue}>{formatBRL(orc.valor_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {orc.observacoes && (
            <p className={styles.orcObs}><em>Obs: {orc.observacoes}</em></p>
          )}

          <div className={styles.orcCardActions}>
            {orc.status === "aberto" && (
              <>
                <button type="button" className={styles.btnStatusAprov}
                  onClick={() => onStatus(orc.id, "aprovado")}>Aprovar</button>
                <button type="button" className={styles.btnStatusRecus}
                  onClick={() => onStatus(orc.id, "recusado")}>Recusar</button>
              </>
            )}

            {podeEditar && (
              <button type="button" className={styles.btnEditar}
                onClick={(e) => { e.stopPropagation(); setEditOpen(true); }}>
                <EditIcon /> Editar
              </button>
            )}

            <PDFDownloadLink
              document={<OrcamentoPDF {...pdfProps} />}
              fileName={`orcamento_${orc.id}.pdf`}
              className={styles.btnPdf}
            >
              {({ loading: pdfLoading }) => pdfLoading ? "Gerando..." : <><FileIcon /> PDF</>}
            </PDFDownloadLink>

            {podeConverter && (
              <button type="button" className={styles.btnConverter} onClick={() => onConverter(orc)}>
                <CartIcon /> Usar como venda
              </button>
            )}

            {orc.status !== "convertido" && (
              <button type="button" className={styles.btnExcluir} onClick={() => onExcluir(orc.id)}>
                <TrashIcon /> Excluir
              </button>
            )}
          </div>
        </div>
      )}
    </div>

      {editOpen && (
        <EditOrcamentoModal
          orc={orc}
          subClientes={subClientes}
          produtos={produtos}
          onSave={handleSaveEdit}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
});
OrcamentoCard.displayName = "OrcamentoCard";

// ─── Hooks ────────────────────────────────────────────────────────────────────

const useCliente = () => {
  const [cliente, setCliente] = useState(null);
  useEffect(() => {
    api.get("/clientes").then(async ({ data }) => {
      let logo_base64 = null;
      if (data.logo_url) {
        const url = data.logo_url.startsWith("http") ? data.logo_url
          : `https://db-fluxoeasy.onrender.com${data.logo_url}`;
        logo_base64 = await toBase64(url);
      }
      setCliente({ ...data, logo_base64 });
    }).catch(() => {});
  }, []);
  return cliente;
};

const useSubClientes = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/sub-clientes").then(({ data }) => setList(data)).catch(() => {});
  }, []);
  return list;
};

const useProdutos = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/estoque"), api.get("/produtos")])
      .then(([estoqueRes, prodRes]) => {
        const estMap = {};
        estoqueRes.data.forEach((e) => {
          if (!estMap[e.produto_id]) estMap[e.produto_id] = { total: 0, valor_venda: e.valor_venda };
          estMap[e.produto_id].total += Number(e.quantidade);
        });
        setList(prodRes.data.map((p) => ({
          ...p,
          valor_venda:    estMap[p.id]?.valor_venda ?? p.preco_custo ?? 0,
          em_estoque:     p.tipo === "servico" ? true : (estMap[p.id]?.total ?? 0) > 0,
          _estoque_total: estMap[p.id]?.total ?? 0,
        })));
      }).catch(() => {});
  }, []);
  return list;
};

const useOrcamentos = () => {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/orcamentos"); setList(data); }
    catch { /* silencioso */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);
  return { list, setList, loading, refetch: fetch };
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const OrcamentoPage = () => {
  const navigate    = useNavigate();
  const cliente     = useCliente();
  const subClientes = useSubClientes();
  const produtos    = useProdutos();
  const { list: orcamentos, setList: setOrcamentos, loading: loadingOrc, refetch } = useOrcamentos();

  const [aba,           setAba]           = useState("novo");
  const [subClienteId,  setSubClienteId]  = useState("");
  const [itens,         setItens]         = useState([EMPTY_ITEM()]);
  const [observacoes,   setObservacoes]   = useState("");
  const [footerMessage, setFooterMessage] = useState("");
  const [desconto,      setDesconto]      = useState(0);
  const [primaryColor,  setPrimaryColor]  = useState(
    () => localStorage.getItem(STORAGE_COLOR_KEY) || "#1A56DB"
  );
  const [saving,       setSaving]       = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("");

  const handleChangeItem  = useCallback((index, patch) => setItens((prev) => prev.map((it, i) => i === index ? { ...it, ...patch } : it)), []);
  const handleAddItem     = useCallback(() => setItens((prev) => [...prev, EMPTY_ITEM()]), []);
  const handleRemoveItem  = useCallback((index) => setItens((prev) => prev.filter((_, i) => i !== index)), []);
  const handleColorChange = useCallback((c) => { setPrimaryColor(c); localStorage.setItem(STORAGE_COLOR_KEY, c); }, []);

  const total = useMemo(() => calcTotal(itens, desconto), [itens, desconto]);

  const subClienteAtual = useMemo(
    () => subClientes.find((s) => String(s.id) === String(subClienteId)) ?? null,
    [subClientes, subClienteId]
  );

  // ── PDF preview ─────────────────────────────────────────────────────────────

  const pdfProps = useMemo(() => ({
    cliente: {
      nome:     cliente?.nome     ?? "",
      cnpj_cpf: cliente?.cnpj_cpf ?? "",
      email:    cliente?.email    ?? "",
      telefone: cliente?.telefone ?? "",
      endereco: cliente?.endereco ?? "",
    },
    logo: cliente?.logo_base64 ?? null,
    subCliente: subClienteAtual
      ? {
          nome:     subClienteAtual.nome     ?? "",
          cpf_cnpj: subClienteAtual.cpf_cnpj ?? "",
          email:    subClienteAtual.email    ?? "",
          telefone: subClienteAtual.telefone ?? "",
          endereco: subClienteAtual.endereco ?? "",
        }
      : null,
    produtos: itens
      .filter((it) => it.nome.trim())
      .map((it) => ({ nome: it.nome, quantidade: it.quantidade, preco: it.valor_unitario })),
    observacoes,
    footerMessage,
    primaryColor,
  }), [cliente, subClienteAtual, itens, observacoes, footerMessage, primaryColor]);

  // ── Salvar ──────────────────────────────────────────────────────────────────

  const handleSalvar = useCallback(async () => {
    if (itens.every((it) => !it.nome.trim())) {
      Swal.fire("Atenção", "Adicione ao menos um item.", "warning");
      return;
    }
    const payload = {
      sub_cliente_id: subClienteId || null,
      observacoes:    observacoes  || null,
      footer_message: footerMessage || null,
      primary_color:  primaryColor,
      desconto:       Number(desconto) || 0,
      itens: itens.filter((it) => it.nome.trim()).map((it) => ({
        produto_id:     it.produto_id || null,
        nome:           it.nome,
        quantidade:     Number(it.quantidade),
        valor_unitario: Number(it.valor_unitario),
        tipo:           it.tipo || "manual",
      })),
    };
    setSaving(true);
    try {
      const { data } = await api.post("/orcamentos", payload);
      if (data.avisos?.length > 0) {
        await Swal.fire({
          title: "Orçamento salvo com avisos", icon: "warning",
          html: `<ul style="text-align:left;font-size:13px">${data.avisos.map((a) => `<li>${a}</li>`).join("")}</ul>`,
          confirmButtonText: "Entendi",
        });
      } else {
        Swal.fire({ icon: "success", title: "Orçamento salvo!", timer: 1400, showConfirmButton: false });
      }
      setOrcamentos((prev) => [data.orcamento, ...prev]);
      setItens([EMPTY_ITEM()]); setSubClienteId(""); setObservacoes(""); setFooterMessage(""); setDesconto(0);
      setAba("salvos");
    } catch (err) {
      Swal.fire("Erro", err.response?.data?.error ?? "Erro ao salvar.", "error");
    } finally { setSaving(false); }
  }, [itens, subClienteId, observacoes, footerMessage, primaryColor, desconto, setOrcamentos]);

  // ── Converter → redireciona para PDV ────────────────────────────────────────

  const handleConverter = useCallback(async (orc) => {
    // 1. Rascunho existente?
    let temRascunho = false;
    try {
      const raw = localStorage.getItem(PDV_STORAGE_KEY);
      temRascunho = raw ? JSON.parse(raw).length > 0 : false;
    } catch {}

    if (temRascunho) {
      const { isConfirmed } = await Swal.fire({
        title: "Rascunho de venda detectado",
        html:  "Existe uma venda em andamento no PDV.<br><br>Deseja <strong>limpar o rascunho</strong> e carregar este orçamento?",
        icon:  "warning",
        showCancelButton:   true,
        confirmButtonColor: "#E53E3E",
        confirmButtonText:  "Sim, limpar e carregar",
        cancelButtonText:   "Cancelar",
      });
      if (!isConfirmed) return;
    }

    // 2. Busca estoque + produtos para resolver itens
    let estoqueAtual = [], produtosAtual = [];
    try {
      const [estRes, prodRes] = await Promise.all([api.get("/estoque"), api.get("/produtos")]);
      estoqueAtual  = estRes.data;
      produtosAtual = prodRes.data;
    } catch {
      Swal.fire("Erro", "Não foi possível carregar o estoque.", "error");
      return;
    }

    // Mapa produto_id → lotes (array, FIFO)
    const estMap = {};
    estoqueAtual.forEach((e) => {
      if (!estMap[e.produto_id]) estMap[e.produto_id] = [];
      estMap[e.produto_id].push(e);
    });

    // 3. Resolve cada item
    const itensPDV   = [];
    const avisosNome = [];

    for (const it of (orc.itens ?? [])) {
      const qtd = Number(it.quantidade);

      // ── Manual ──────────────────────────────────────────────────────────────
      if (!it.produto_id || it.tipo === "manual") {
        itensPDV.push({
          _tipo:        "manual",
          _semEstoque:  false,
          estoque_id:   `manual_${it.nome}_${Math.random()}`,
          produto_id:   null,
          produto_nome: it.nome,
          ean:          null,
          valor_venda:  Number(it.valor_unitario),
          _estoque_max: Infinity,
          quantidade:   qtd,
        });
        continue;
      }

      // ── Serviço ─────────────────────────────────────────────────────────────
      if (it.tipo === "servico") {
        const srv = produtosAtual.find((p) => p.id === it.produto_id && p.tipo === "servico");
        if (!srv) {
          avisosNome.push(`"${it.nome}" — serviço não encontrado`);
          itensPDV.push({
            _tipo:        "servico",
            _semEstoque:  true,
            estoque_id:   `srv_nf_${it.produto_id}`,
            produto_id:   it.produto_id,
            produto_nome: it.nome,
            ean:          null,
            valor_venda:  Number(it.valor_unitario),
            _estoque_max: Infinity,
            quantidade:   qtd,
          });
          continue;
        }
        itensPDV.push({
          _tipo:        "servico",
          _semEstoque:  false,
          estoque_id:   `srv_${srv.id}`,
          produto_id:   srv.id,
          produto_nome: srv.nome,
          ean:          null,
          valor_venda:  Number(it.valor_unitario),
          _estoque_max: Infinity,
          quantidade:   qtd,
        });
        continue;
      }

      // ── Produto físico ───────────────────────────────────────────────────────
      const lotes = estMap[it.produto_id] ?? [];
      const totalDisponivel = lotes.reduce((a, l) => a + Number(l.quantidade), 0);

      if (lotes.length === 0 || totalDisponivel === 0) {
        avisosNome.push(`"${it.nome}" — sem estoque disponível`);
        itensPDV.push({
          _tipo:        "produto",
          _semEstoque:  true,
          estoque_id:   `nf_${it.produto_id}`,
          produto_id:   it.produto_id,
          produto_nome: it.nome,
          ean:          null,
          valor_venda:  Number(it.valor_unitario),
          _estoque_max: 0,
          quantidade:   qtd,
        });
        continue;
      }

      if (qtd > totalDisponivel) {
        avisosNome.push(`"${it.nome}" — solicitado ${qtd}, disponível ${totalDisponivel}`);
      }

      const lote = lotes[0];
      itensPDV.push({
        _tipo:        "produto",
        _semEstoque:  false,
        estoque_id:   lote.id,
        produto_id:   it.produto_id,
        produto_nome: it.nome,
        ean:          lote.ean ?? null,
        valor_venda:  Number(it.valor_unitario),
        _estoque_max: Number(lote.quantidade),
        quantidade:   Math.min(qtd, Number(lote.quantidade)),
      });
    }

    // 4. Avisa sobre itens problemáticos
    if (avisosNome.length > 0) {
      const { isConfirmed } = await Swal.fire({
        title: "Itens com problema",
        icon:  "warning",
        html: `
          <p style="font-size:13px;margin-bottom:10px">Os itens abaixo não foram encontrados em estoque e aparecerão marcados no PDV:</p>
          <ul style="text-align:left;font-size:13px;color:#D97706;line-height:2">
            ${avisosNome.map((a) => `<li>${a}</li>`).join("")}
          </ul>
          <p style="font-size:12px;margin-top:10px;color:#8FA3BF">Você pode continuar e remover os itens no PDV antes de finalizar.</p>
        `,
        showCancelButton:  true,
        confirmButtonText: "Continuar no PDV",
        cancelButtonText:  "Cancelar",
      });
      if (!isConfirmed) return;
    }

    // 5. Grava no localStorage e navega
    try {
      localStorage.setItem(PDV_STORAGE_KEY, JSON.stringify(itensPDV));
      localStorage.removeItem(PDV_DESCONTO_KEY);
      localStorage.setItem(PDV_ORC_KEY, String(orc.id));
    } catch {
      Swal.fire("Erro", "Não foi possível gravar os itens no armazenamento.", "error");
      return;
    }

    navigate("/venda");
  }, [navigate]);

  // ── Status / Excluir / Editar ───────────────────────────────────────────────

  const handleStatus = useCallback(async (id, status) => {
    try {
      await api.put(`/orcamentos/${id}/status`, { status });
      setOrcamentos((prev) => prev.map((o) => o.id === id ? { ...o, status } : o));
    } catch { Swal.fire("Erro", "Não foi possível atualizar o status.", "error"); }
  }, [setOrcamentos]);

  const handleEditar = useCallback(async (id, payload) => {
    try {
      const { data } = await api.put(`/orcamentos/${id}`, payload);
      if (data.avisos?.length > 0) {
        await Swal.fire({
          title: "Salvo com avisos", icon: "warning",
          html: `<ul style="text-align:left;font-size:13px">${data.avisos.map((a) => `<li>${a}</li>`).join("")}</ul>`,
          confirmButtonText: "Entendi",
        });
      } else {
        Swal.fire({ icon: "success", title: "Orçamento atualizado!", timer: 1400, showConfirmButton: false });
      }
      setOrcamentos((prev) => prev.map((o) => o.id === id ? { ...o, ...data.orcamento } : o));
    } catch (err) {
      Swal.fire("Erro", err.response?.data?.error ?? "Erro ao atualizar.", "error");
      throw err;
    }
  }, [setOrcamentos]);

  const handleExcluir = useCallback(async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Excluir orçamento?", icon: "warning",
      showCancelButton: true, confirmButtonColor: "#E53E3E",
      confirmButtonText: "Sim, excluir", cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/orcamentos/${id}`);
      setOrcamentos((prev) => prev.filter((o) => o.id !== id));
      Swal.fire({ icon: "success", title: "Excluído!", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Erro", err.response?.data?.error ?? "Erro ao excluir.", "error");
    }
  }, [setOrcamentos]);

  const orcamentosFiltrados = useMemo(() =>
    filtroStatus ? orcamentos.filter((o) => o.status === filtroStatus) : orcamentos,
    [orcamentos, filtroStatus]
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>

      <div className={styles.tabs}>
        <button type="button"
          className={`${styles.tab} ${aba === "novo" ? styles.tabActive : ""}`}
          onClick={() => setAba("novo")}>
          <PlusIcon /> Novo orçamento
        </button>
        <button type="button"
          className={`${styles.tab} ${aba === "salvos" ? styles.tabActive : ""}`}
          onClick={() => setAba("salvos")}>
          <FileIcon /> Orçamentos salvos
          {orcamentos.length > 0 && <span className={styles.tabBadge}>{orcamentos.length}</span>}
        </button>
      </div>

      {/* ══ ABA NOVO ══════════════════════════════════════════════════════════ */}
      {aba === "novo" && (
        <div className={styles.card}>
          <div className={styles.pageHeader}>
            <div>
              <h1 className={styles.pageTitle}>Novo Orçamento</h1>
              <p className={styles.pageSubtitle}>{cliente?.nome ?? ""}</p>
            </div>
          </div>

          {/* Cliente */}
          <section aria-label="Cliente">
            <h2 className={styles.sectionTitle}>Para quem é este orçamento?</h2>
            <select className={styles.select} value={subClienteId}
              onChange={(e) => setSubClienteId(e.target.value)}>
              <option value="">— Sem cliente vinculado —</option>
              {subClientes.map((sc) => (
                <option key={sc.id} value={sc.id}>{sc.nome}</option>
              ))}
            </select>

            {subClienteAtual && (
              <div className={styles.subClientePreview}>
                <div className={styles.subClientePreviewIcon}><UserIcon /></div>
                <div className={styles.subClientePreviewInfo}>
                  <strong>{subClienteAtual.nome}</strong>
                  {subClienteAtual.cpf_cnpj && <span>{subClienteAtual.cpf_cnpj}</span>}
                  {subClienteAtual.email     && <span>{subClienteAtual.email}</span>}
                  {subClienteAtual.telefone  && <span>{subClienteAtual.telefone}</span>}
                  {subClienteAtual.endereco  && <span>{subClienteAtual.endereco}</span>}
                </div>
              </div>
            )}
          </section>

          {/* Itens */}
          <section aria-label="Itens do orçamento">
            <h2 className={styles.sectionTitle}>Itens</h2>
            <div className={styles.tabelaWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Produto / Serviço</th>
                    <th style={{ width: 80 }}>Qtd.</th>
                    <th style={{ width: 130 }}>Preço unit. (R$)</th>
                    <th style={{ width: 110 }}>Subtotal</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, i) => (
                    <ItemRow key={i} index={i} item={it}
                      onChange={handleChangeItem} onRemove={handleRemoveItem} produtos={produtos} />
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className={styles.totalLabel}>
                      <div className={styles.descontoRow}>
                        <span>Desconto (R$)</span>
                        <input type="number" className={styles.inputDesconto}
                          min={0} step={0.01} value={desconto}
                          onChange={(e) => setDesconto(parseFloat(e.target.value) || 0)} />
                      </div>
                    </td>
                    <td colSpan={2} className={styles.totalValue}>{formatBRL(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <button type="button" className={styles.btnAddItem} onClick={handleAddItem}>
              <PlusIcon /> Adicionar item
            </button>
          </section>

          {/* Complementos */}
          <section aria-label="Complementos">
            <h2 className={styles.sectionTitle}>Complementos</h2>
            <div className={styles.complementosGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Observações</label>
                <textarea className={styles.textarea} rows={3}
                  placeholder="Condições de pagamento, validade..."
                  value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Mensagem de rodapé</label>
                <input type="text" className={styles.input}
                  placeholder="Ex: Agradecemos a preferência!"
                  value={footerMessage} onChange={(e) => setFooterMessage(e.target.value)} />
              </div>
            </div>
          </section>

          {/* Cor */}
          <section aria-label="Aparência do PDF">
            <h2 className={styles.sectionTitle}>Aparência do PDF</h2>
            <ColorPicker value={primaryColor} onChange={handleColorChange} />
          </section>

          {/* Ações */}
          <div className={styles.actionsRow}>
            <PDFDownloadLink
              document={<OrcamentoPDF {...pdfProps} />}
              fileName="orcamento_preview.pdf"
              className={styles.btnDownload}
            >
              {({ loading: pdfL }) => pdfL ? "Gerando..." : <><FileIcon /> Baixar PDF preview</>}
            </PDFDownloadLink>

            <button type="button" className={styles.btnSalvar}
              onClick={handleSalvar} disabled={saving} aria-busy={saving}>
              {saving
                ? <><span className={styles.spinner} /> Salvando...</>
                : <><SaveIcon /> Salvar orçamento</>}
            </button>
          </div>

          <section aria-label="Preview do PDF">
            <h2 className={styles.sectionTitle}>Preview</h2>
            <div className={styles.pdfWrapper}>
              <PDFViewer width="100%" height={520}
                key={JSON.stringify(itens.map((it) => ({ n: it.nome, q: it.quantidade, v: it.valor_unitario })))}>
                <OrcamentoPDF {...pdfProps} />
              </PDFViewer>
            </div>
          </section>
        </div>
      )}

      {/* ══ ABA SALVOS ════════════════════════════════════════════════════════ */}
      {aba === "salvos" && (
        <div className={styles.card}>
          <div className={styles.pageHeader}>
            <div>
              <h1 className={styles.pageTitle}>Orçamentos salvos</h1>
              <p className={styles.pageSubtitle}>{orcamentos.length} registros</p>
            </div>
            <button type="button" className={styles.btnRefresh}
              onClick={refetch} disabled={loadingOrc} aria-label="Atualizar">
              <RefreshIcon />
            </button>
          </div>

          <div className={styles.filtroRow}>
            {["", "aberto", "aprovado", "recusado", "convertido"].map((s) => (
              <button key={s} type="button"
                className={`${styles.filtroBtn} ${filtroStatus === s ? styles.filtroBtnActive : ""}`}
                onClick={() => setFiltroStatus(s)}>
                {s === "" ? "Todos" : STATUS_LABEL[s]}
              </button>
            ))}
          </div>

          {loadingOrc ? (
            <div className={styles.stateBox}><span className={styles.spinner} /> Carregando...</div>
          ) : orcamentosFiltrados.length === 0 ? (
            <div className={styles.emptyState}><FileIcon /><p>Nenhum orçamento encontrado.</p></div>
          ) : (
            <div className={styles.orcList}>
              {orcamentosFiltrados.map((orc) => (
                <OrcamentoCard key={orc.id} orc={orc} cliente={cliente}
                  subClientes={subClientes} produtos={produtos}
                  onConverter={handleConverter} onExcluir={handleExcluir}
                  onStatus={handleStatus} onEditar={handleEditar} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrcamentoPage;